/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Field, ErrorMessage } from 'formik';
import {
   Grid,
   Box,
   Typography,
   MenuItem,
   Select,
   FormControl,
   InputLabel,
   FormHelperText,
   Chip,
   TextField,
} from '@mui/material';
import CustomDatePicker from '../../../Components/MUI/CustomDatePicker';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../../Utils/RichEditor';
import { useDispatch, useSelector } from 'react-redux';
import {
   setContractCurrency,
   setSelectedContractTypeId,
   setContractTypePreFillData,
} from '../../../Redux/Reducers/Contracts/ContractSlice';
import InfiniteAutocomplete from '../../../Components/InfiniteAutocomplete';
import { useGetopportunitiesQuery } from '../../../Redux/Service/OpportunityApi';
import { useGetProvidersQuery } from '../../../Redux/Service/ProviderApi';
import axios from 'axios';
import { API_URL } from '../../../Config/index';
import { getAuthToken } from '../../../Redux/Service/FetchBaseQuery';
import dayjs from 'dayjs'; // already imported
import Swal from 'sweetalert2';
import {
   useGetActiveContractTypesQuery,
   useGetContractTypeByIdQuery,
} from '../../../Redux/Service/ContractTypeApi';
import { extractPreFillValues } from '../../../Utils/contractFieldHelpers';

const CONTRACT_TYPE_PREFILL_FIELDS = [
   'confidential_information',
   'amendments',
   'general_terms_and_conditions',
   'project_termination_terms',
   'payment_terms',
   'application_ownership',
   'liability',
   'legal_jurisdiction',
];

// NEW: per-field max-length limits, matching the submit-time validation
const FIELD_MAX_LENGTHS = {
   confidential_information: 5000,
   amendments: 5000,
   scope_of_work: 1000,
   purpose_of_the_agreement: 1000,
};

function StepOneUpdate({
   isSubmitting,
   values,
   setFieldValue,
   touched,
   errors,
   setFieldTouched,
   initialValues,
   handleBlur,
   activeStep,
   loadedContractIdRef, // Persistent ref from ContractUpdate — survives step navigation remounts
}) {
   // Redux State for Contract Prefilled Data
   const contractPrefilledData = useSelector(
      (state) => state.contractState.contractUpdatePrefilledData,
   );
   const contractTypePreFillData = useSelector(
      (state) => state.contractState.contractTypePreFillData,
   );
   const contractData = useSelector((state) => state.contractState);
   const [leadId, setLeadId] = useState('');
   const dispatch = useDispatch();
   const previousTypeIdRef = useRef(null);

   // NEW: live character-length errors for the 4 limited CKEditor fields
   const [fieldLengthErrors, setFieldLengthErrors] = useState({});

   // NEW: checks length against that field's own limit and stores/clears the error message
   const checkLengthError = (fieldName, value) => {
      const length = value?.length || 0;
      const maxLength = FIELD_MAX_LENGTHS[fieldName];
      setFieldLengthErrors((prev) => ({
         ...prev,
         [fieldName]:
            maxLength && length > maxLength
               ? `This field is too long. Please keep it within ${maxLength} characters.`
               : '',
      }));
   };

   // Contract Type - RTK Query
   const { data: activeContractTypes, isLoading: isLoadingContractTypes } =
      useGetActiveContractTypesQuery();
   const contractTypeList = activeContractTypes?.response || [];

   const selectedContractTypeIdValue = values.contractTypeId || '';
   const { data: contractTypeDetail } = useGetContractTypeByIdQuery(selectedContractTypeIdValue, {
      skip: !selectedContractTypeIdValue,
   });

   // When contract type detail loads after a USER-triggered change, overwrite terms fields
   // CRITICAL: Only apply pre-fill when user explicitly selected a type, not on component remount
   useEffect(() => {
      if (!contractTypeDetail) return;

      // Only apply if flag indicates user just selected this type
      // (not if component is remounting with same type already selected)
      if (!previousTypeIdRef.current?.shouldApplyPreFill) {
         return;
      }

      if (String(previousTypeIdRef.current?.typeId || '') !== String(selectedContractTypeIdValue)) {
         return;
      }

      CONTRACT_TYPE_PREFILL_FIELDS.forEach((key) => {
         setFieldValue(key, '');
      });

      const fieldValues =
         contractTypeDetail?.response?.field_values || contractTypeDetail?.data?.field_values || [];
      const preFill = extractPreFillValues(fieldValues);
      dispatch(setContractTypePreFillData(preFill));

      Object.entries(preFill).forEach(([key, value]) => {
         setFieldValue(key, value || '');
      });

      // Clear the flag after applying
      previousTypeIdRef.current = { shouldApplyPreFill: false, typeId: null };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contractTypeDetail]);

   // Handle contract type change with SweetAlert confirmation
   const handleContractTypeChange = async (e) => {
      const typeId = e.target.value;

      const result = await Swal.fire({
         title: 'Change Contract Type?',
         text: "Changing the contract type will overwrite the Terms & Conditions fields with the new type's pre-filled content. Continue?",
         icon: 'warning',
         showCancelButton: true,
         confirmButtonText: 'Yes, change it',
         cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) {
         return;
      }

      // Set flag to indicate user just selected a type, so effect should apply pre-fill
      previousTypeIdRef.current = {
         shouldApplyPreFill: true,
         typeId: typeId ? String(typeId) : '',
      };

      CONTRACT_TYPE_PREFILL_FIELDS.forEach((key) => {
         setFieldValue(key, '');
      });
      dispatch(setContractTypePreFillData(null));

      setFieldValue('contractTypeId', typeId);
      dispatch(setSelectedContractTypeId(typeId ? Number(typeId) : null));
   };

   // States for InfiniteAutocomplete
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const debounceTimeout = useRef(null);

   // State for milestones, estimates, proposals
   const [milestones, setMilestones] = useState([]);
   const [isLoadingMilestones, setIsLoadingMilestones] = useState(false);
   const [estimates, setEstimates] = useState([]);
   const [isLoadingEstimates, setIsLoadingEstimates] = useState(false);
   const [proposals, setProposals] = useState([]);
   const [isLoadingProposals, setIsLoadingProposals] = useState(false);

   // Use RTK Query with fixed parameters
   const { data: opportunityData, isLoading: isLoadingForOpportunity } = useGetopportunitiesQuery(
      { limit: 10, name: search, page },
      { skip: search === '' && page === 1 && visibleOptions.length > 0 },
   );

   // Service Provider dropdown logic
   const {
      data: providerData,
      isLoading: isLoadingForProvider,
      isError: isErrorForProvider,
      error: errorForProvider,
   } = useGetProvidersQuery();

   // Fetch milestones
   useEffect(() => {
      const fetchMilestones = async () => {
         setIsLoadingMilestones(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(`${API_URL}milestone/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.data && response.data.response && response.data.response.data) {
               setMilestones(response.data.response.data);
            } else {
               setMilestones([]);
            }
         } catch (error) {
            console.error('Error fetching milestones:', error);
            setMilestones([]);
         } finally {
            setIsLoadingMilestones(false);
         }
      };

      fetchMilestones();
   }, []);

   // Fetch estimates
   useEffect(() => {
      const fetchEstimates = async () => {
         setIsLoadingEstimates(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(`${API_URL}estimate/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.data && response.data.response && response.data.response.data) {
               setEstimates(response.data.response.data);
            } else {
               setEstimates([]);
            }
         } catch (error) {
            console.error('Error fetching estimates:', error);
            setEstimates([]);
         } finally {
            setIsLoadingEstimates(false);
         }
      };

      fetchEstimates();
   }, []);

   // Fetch proposals
   useEffect(() => {
      const fetchProposals = async () => {
         setIsLoadingProposals(true);
         try {
            const token = getAuthToken();
            const response = await axios.get(
               `${API_URL}proposal/list?limit=10&page=0&orderBy=DESC&search=`,
               {
                  headers: {
                     Authorization: `Bearer ${token}`,
                  },
               },
            );

            if (response.data && response.data.response && response.data.response.data) {
               setProposals(response.data.response.data);
            } else {
               setProposals([]);
            }
         } catch (error) {
            console.error('Error fetching proposals:', error);
            setProposals([]);
         } finally {
            setIsLoadingProposals(false);
         }
      };

      fetchProposals();
   }, []);

   // Sync form with API data whenever contractPrefilledData changes (new contract loaded)
   // Uses loadedContractIdRef (from ContractUpdate) so the "already loaded" flag survives
   // step navigation unmount/remount cycles.
   useEffect(() => {
      if (contractPrefilledData && contractPrefilledData.id !== loadedContractIdRef?.current) {
         // New contract data loaded, update all fields

         // Opportunity
         if (contractPrefilledData.opportunity) {
            const opportunity = {
               ...contractPrefilledData.opportunity,
               name: contractPrefilledData.opportunity.name || 'Unknown',
               id: contractPrefilledData.opportunity.id,
            };
            setSelectedOption(opportunity);
            setLeadId(opportunity.id);
            setFieldValue('lead_id', opportunity.id);
            setFieldValue('opportunity_name', opportunity.name);
            setVisibleOptions((prevOptions) => {
               const existingIds = new Set(prevOptions.map((opt) => opt.id));
               if (!existingIds.has(opportunity.id)) {
                  return [...prevOptions, opportunity];
               }
               return prevOptions;
            });
         }

         // Set all fields from API response.
         // Skip pre-fill-controlled fields if the user already picked a different contract type.
         if (!contractTypePreFillData) {
            setFieldValue('confidential_information', contractPrefilledData.confidentialInfo || '');
            setFieldValue('amendments', contractPrefilledData.amendment || '');
         }
         setFieldValue('purpose_of_the_agreement', contractPrefilledData.purposeAgreement || '');
         setFieldValue('scope_of_work', contractPrefilledData.scopeWork || '');
         setFieldValue('quotation_id', contractPrefilledData.quotation?.id || '');
         setFieldValue('proposal', contractPrefilledData.proposal?.id || '');
         setFieldValue('milestone', contractPrefilledData.milestones?.id || '');
         setFieldValue(
            'estimate',
            contractPrefilledData.estimate?.id ||
            contractPrefilledData.quotation?.estimate?.id ||
            '',
         );
         setFieldValue('total_development_cost', contractPrefilledData.billableAmount || 0);
         setFieldValue(
            'payable_tax_percentage',
            contractPrefilledData.taxAmount && contractPrefilledData.billableAmount
               ? (contractPrefilledData.taxAmount / contractPrefilledData.billableAmount) * 100
               : 0,
         );
         setFieldValue('project_development', contractPrefilledData.developementTime || '');
         setFieldValue('projectName', contractPrefilledData.projectName || '');
         setFieldValue('client_name', contractPrefilledData.client_name || '');
         setFieldValue('companyName', contractPrefilledData.companyName || '');
         setFieldValue('designation', contractPrefilledData.designation || '');
         setFieldValue('address', contractPrefilledData.address || '');

         // Set Contract Type — only overwrite when no manual type change has been made.
         // If contractTypePreFillData is set in Redux it means the user already picked a
         // different contract type in this session; preserve that choice.
         if (contractPrefilledData.contract_type_id && !contractTypePreFillData) {
            setFieldValue('contractTypeId', contractPrefilledData.contract_type_id);
            previousTypeIdRef.current = null; // Mark as initial DB load — don't trigger pre-fill
         }

         // Set Service Provider
         if (contractPrefilledData.serviceProvider) {
            setFieldValue('serviceProviderId', contractPrefilledData.serviceProvider.id);
         }

         // Currency logic
         if (contractPrefilledData.currency) {
            dispatch(setContractCurrency(contractPrefilledData.currency));
         } else {
            dispatch(
               setContractCurrency(
                  contractData.allQuotations.find(
                     (quotation) => quotation.id === contractPrefilledData?.quotation?.id,
                  )?.currency,
               ),
            );
         }

         // Mark this contract as loaded in the persistent ref
         if (loadedContractIdRef) loadedContractIdRef.current = contractPrefilledData.id;
      }
   }, [
      contractPrefilledData,
      contractTypePreFillData,
      setFieldValue,
      dispatch,
      contractData.allQuotations,
      loadedContractIdRef,
   ]);

   // Always sync selectedOption with current Formik values when step changes or values change
   useEffect(() => {
      // If lead_id exists, try to find it in visibleOptions or use fallback
      if (values.lead_id) {
         const found = visibleOptions.find((opt) => opt.id === values.lead_id);
         if (found) {
            setSelectedOption(found);
         } else if (selectedOption === null || selectedOption?.id !== values.lead_id) {
            // Fallback: create option object with current values
            setSelectedOption({
               id: values.lead_id,
               name: values.opportunity_name || contractPrefilledData?.opportunity?.name || '',
            });
         }
      } else {
         setSelectedOption(null);
      }
   }, [values.lead_id, visibleOptions, values.opportunity_name, activeStep, contractPrefilledData]);

   // Load opportunity data into visible options
   useEffect(() => {
      if (opportunityData?.response?.data) {
         const newData = opportunityData.response.data;
         setVisibleOptions((prevOptions) => {
            const existingIds = new Set(prevOptions.map((opt) => opt.id));
            const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
            return [...prevOptions, ...uniqueNewData];
         });
      }
   }, [opportunityData?.response?.data]);

   // Handle scrolling for infinite loading
   const handleScroll = (event) => {
      if (isLoadingForOpportunity) return;

      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   // Handle lead selection - Enhanced to store name for fallback
   const handleSelectLead = (event, value, reason) => {
      if (
         (reason === 'selectOption' && value?.id !== selectedOption?.id) ||
         (reason === 'clear' && selectedOption !== null)
      ) {
         setSelectedOption(value);
         setFieldValue('lead_id', value ? value.id : null);
         setFieldValue('opportunity_name', value ? value.name : ''); // Store name for fallback
         if (value) {
            setLeadId(value.id);
         }
      }
   };

   // Optimize search to prevent unnecessary API calls
   const handleSearch = (event, value) => {
      if (value === search) return;

      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
         setPage(1);
         if (value !== search) {
            setVisibleOptions([]);
         }
      }, 500);
   };

   // Handle Add Quotation - only update if user hasn't already selected a different lead
   const handleAddQuotation = (quotationId) => {
      const selectedQuotation = contractData?.allQuotations?.find(
         (quotation) => quotation?.id === quotationId,
      );
      if (selectedQuotation) {
         setFieldValue('quotation_id', quotationId);

         // Only update lead_id if it's currently empty (don't override user's explicit selection)
         if (!values.lead_id) {
            setFieldValue('lead_id', selectedQuotation?.estimate?.lead?.id);
         }

         // Auto-populate service provider from selected quotation
         if (selectedQuotation?.serviceProvider?.id) {
            setFieldValue('serviceProviderId', selectedQuotation.serviceProvider.id);
         }

         // Update currency
         dispatch(setContractCurrency(selectedQuotation?.currency));
      } else {
         console.error('Quotation not found for ID:', quotationId);
      }
   };

   // Handle dropdown selections - preserve user choices
   const handleMilestoneChange = (e) => {
      setFieldValue('milestone', e.target.value);
   };

   const handleEstimateChange = (e) => {
      setFieldValue('estimate', e.target.value);
   };

   const handleProposalChange = (e) => {
      setFieldValue('proposal', e.target.value);
   };

   // Helper function to handle possible JSON/Array/String/Null inputs for CKEditor
   const getCurrentValue = (fieldName) => {
      const value = values && values[fieldName];
      if (value === undefined || value === null) return '';
      return typeof value === 'string' ? value : String(value || '');
   };

   // Safe wrapper for CKEditor data prop to prevent crashes from undefined/null values
   const renderCKEditor = (fieldName) => {
      const dataValue = getCurrentValue(fieldName);
      const safeData = typeof dataValue === 'string' ? dataValue : '';

      return (
         <CKEditor
            key={`ckeditor-s1-${fieldName}`}
            editor={ClassicEditor}
            data={safeData}
            onBlur={(event, editor) => {
               const data = editor.getData();
               if (values[fieldName] !== data) {
                  setFieldValue(fieldName, data);
               }
               checkLengthError(fieldName, data); {/* NEW */ }
               setFieldTouched(fieldName, true);
            }}
            onReady={(editor) => {
               const currentValue = getCurrentValue(fieldName);
               if (currentValue && editor.getData() !== currentValue) {
                  editor.setData(currentValue);
               }
            }}
         />
      );
   };

   return (
      <div className="card stepCard">
         <Box className="card-body" sx={{ padding: '15px' }}>
            <Grid container spacing={2}>
               <Grid item md={12} xs={12}>
                  <Box className="commonBox">
                     <Grid container spacing={4}>
                        {/* Contract Type */}
                        <Grid item md={12} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Contract Type{' '}
                              <span style={{ color: '#d32f2f', paddingLeft: '5px' }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <InputLabel id="contract-type-update-label">
                                 Select Contract Type
                              </InputLabel>
                              <Select
                                 labelId="contract-type-update-label"
                                 label="Select Contract Type"
                                 value={values.contractTypeId || ''}
                                 onChange={handleContractTypeChange}
                                 disabled={isLoadingContractTypes}
                              >
                                 <MenuItem value="">
                                    <em>Select a contract type</em>
                                 </MenuItem>
                                 {contractTypeList.map((ct) => (
                                    <MenuItem key={ct.id} value={ct.id}>
                                       {ct.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>
                        {/* Opportunity Details */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Opportunity Details{' '}
                              <span
                                 style={{
                                    color: '#d32f2f',
                                    paddingLeft: '5px',
                                 }}
                              >
                                 *
                              </span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>

                           <FormControl
                              fullWidth
                              error={Boolean(touched.lead_id && errors.lead_id)}
                           >
                              <InfiniteAutocomplete
                                 required
                                 onBlur={handleBlur}
                                 value={selectedOption}
                                 onChange={(event, value, reason) =>
                                    handleSelectLead(event, value, reason)
                                 }
                                 options={visibleOptions}
                                 onScroll={handleScroll}
                                 inputValue={search}
                                 setInputValue={handleSearch}
                                 total_options={'Opportunity'}
                                 loading={isLoadingForOpportunity}
                                 getOptionLabel={(option) => option.name || ''}
                                 isOptionEqualToValue={(option, value) => option.id === value.id}
                                 renderOption={(props, option) => (
                                    <li {...props} key={option.id} style={{ padding: '8px 16px' }}>
                                       <div
                                          style={{
                                             display: 'flex',
                                             flexDirection: 'column',
                                             width: '100%',
                                             overflow: 'hidden',
                                          }}
                                       >
                                          <Typography
                                             variant="body1"
                                             fontWeight="medium"
                                             sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                             }}
                                          >
                                             {option.name}
                                          </Typography>
                                          <Typography
                                             variant="caption"
                                             color="text.secondary"
                                             sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                             }}
                                          >
                                             {option.company?.name || 'No company'} •{' '}
                                             {option.status || 'No status'}
                                          </Typography>
                                       </div>
                                    </li>
                                 )}
                                 sx={{
                                    '& .MuiAutocomplete-inputRoot': {
                                       paddingTop: '8px',
                                       paddingBottom: '8px',
                                    },
                                    '& .MuiInputLabel-root': {
                                       transform: selectedOption
                                          ? 'translate(14px, -9px) scale(0.75)'
                                          : 'translate(14px, 16px) scale(1)',
                                    },
                                    '& .MuiAutocomplete-option': {
                                       paddingTop: '6px',
                                       paddingBottom: '6px',
                                    },
                                 }}
                              />
                              {touched.lead_id && errors.lead_id && (
                                 <FormHelperText>{errors.lead_id}</FormHelperText>
                              )}
                           </FormControl>
                        </Grid>

                        {/* Select Quotation */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Select Quotation
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="quotation-select-label"
                                 label="Select Quotation"
                                 onChange={(e) => handleAddQuotation(e.target.value)}
                                 value={values.quotation_id || ''}
                                 displayEmpty
                              >
                                 <MenuItem value="">Select Quotation</MenuItem>
                                 {contractData?.allQuotations?.map((option, index) => (
                                    <MenuItem key={option?.id} value={option?.id}>
                                       {contractData?.allQuotations[index]?.title}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>

                        {/* Milestone */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Milestone
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="milestone-select-label"
                                 label="Select Milestone"
                                 name="milestone"
                                 value={values.milestone || ''}
                                 onChange={handleMilestoneChange}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingMilestones
                                       ? 'Loading milestones...'
                                       : 'Select Milestone'}
                                 </MenuItem>
                                 {milestones.map((milestone) => (
                                    <MenuItem key={milestone.id} value={milestone.id}>
                                       {milestone.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>

                        {/* Estimate */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Estimate
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="estimate-select-label"
                                 label="Select Estimate"
                                 name="estimate"
                                 value={values.estimate || ''}
                                 onChange={handleEstimateChange}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingEstimates
                                       ? 'Loading estimates...'
                                       : 'Select Estimate'}
                                 </MenuItem>
                                 {estimates.map((estimate) => (
                                    <MenuItem key={estimate.id} value={estimate.id}>
                                       {estimate.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>

                        {/* Proposal */}
                        <Grid item md={6} xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Proposal
                              <Chip
                                 label="Optional"
                                 color="primary"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                    background: '#3F30FF',
                                    color: '#fff',
                                 }}
                              />
                           </Typography>
                           <FormControl fullWidth>
                              <Select
                                 labelId="proposal-select-label"
                                 label="Select Proposal"
                                 name="proposal"
                                 value={values.proposal ? String(values.proposal) : ''}
                                 onChange={handleProposalChange}
                                 displayEmpty
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingProposals
                                       ? 'Loading proposals...'
                                       : 'Select Proposal'}
                                 </MenuItem>
                                 {proposals.map((proposal) => (
                                    <MenuItem key={proposal.id} value={proposal.id}>
                                       {proposal.title}
                                    </MenuItem>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>

                        {/* Service Provider */}
                        <Grid item md={6} xs={12}>
                           <Typography variant="h6" gutterBottom align="left">
                              Service Provider <span style={{ color: '#d32f2f' }}>*</span>
                           </Typography>
                           <FormControl
                              fullWidth
                              error={Boolean(touched.serviceProviderId && errors.serviceProviderId)}
                           >
                              <InputLabel id="service-provider-label">Service Provider</InputLabel>
                              <Select
                                 labelId="service-provider-label"
                                 label="Service Provider"
                                 name="serviceProviderId"
                                 value={values.serviceProviderId || ''}
                                 onChange={(e) =>
                                    setFieldValue('serviceProviderId', Number(e.target.value))
                                 }
                                 disabled={isLoadingForProvider}
                                 required
                              >
                                 <MenuItem value="" disabled>
                                    {isLoadingForProvider
                                       ? 'Loading...'
                                       : 'Select Service Provider'}
                                 </MenuItem>
                                 {providerData?.response?.map((provider) => (
                                    <MenuItem key={provider.id} value={provider.id}>
                                       {provider.name}
                                    </MenuItem>
                                 ))}
                              </Select>
                              {touched.serviceProviderId && errors.serviceProviderId && (
                                 <FormHelperText>{errors.serviceProviderId}</FormHelperText>
                              )}
                           </FormControl>
                        </Grid>

                        {/* Confidential Information */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Confidential Information{' '}
                              <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="confidential_information">
                                    {({ field }) => {
                                       return (<>
                                          {renderCKEditor('confidential_information')}
                                          {field.value.length > 0 && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {field.value?.length} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.confidential_information && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.confidential_information}
                                             </Typography>
                                          )}
                                          {/* {touched.confidential_information && errors.confidential_information && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {errors.confidential_information}
                                             </Typography>
                                          )} */}
                                       </>)
                                    }
                                    }
                                 </Field>
                                 {/* <ErrorMessage
                                    name="confidential_information"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Purpose of the Agreement */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Purpose of the Agreement{' '}
                              <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="purpose_of_the_agreement">
                                    {({ field }) => {
                                       return (<>
                                          {renderCKEditor('purpose_of_the_agreement')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {field.value?.length} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.purpose_of_the_agreement && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.purpose_of_the_agreement}
                                             </Typography>
                                          )}
                                          {/* {touched.purpose_of_the_agreement && errors.purpose_of_the_agreement && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {errors.purpose_of_the_agreement}
                                             </Typography>
                                          )} */}
                                       </>)
                                    }
                                    }
                                 </Field>
                                 {/* <ErrorMessage
                                    name="purpose_of_the_agreement"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Scope of work */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Scope of work
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="scope_of_work">
                                    {({ field }) => {
                                       return (<>
                                          {renderCKEditor('scope_of_work')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {field.value?.length} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.scope_of_work && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.scope_of_work}
                                             </Typography>
                                          )}
                                          {/* {touched.scope_of_work && errors.scope_of_work && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {errors.scope_of_work}
                                             </Typography>
                                          )} */}
                                       </>)
                                    }
                                    }
                                 </Field>
                                 {/* <ErrorMessage
                                    name="scope_of_work"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Amendments */}
                        <Grid item xs={12}>
                           <Typography
                              variant="h6"
                              gutterBottom
                              align="left"
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Amendments <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span>
                              <Chip
                                 label="Required"
                                 color="error"
                                 size="small"
                                 sx={{
                                    ml: 1,
                                    fontSize: '0.7rem',
                                    height: '20px',
                                 }}
                              />
                           </Typography>
                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Field name="amendments">
                                    {({ field }) => {
                                       return (<>
                                          {renderCKEditor('amendments')}
                                          {field.value && (
                                             <Typography
                                                variant="caption"
                                                color="GrayText"
                                                sx={{ mt: 0.5, display: 'block' }}
                                             >
                                                {field.value?.length} characters
                                             </Typography>
                                          )}
                                          {/* NEW: red length-limit error, shown like a helper text below the field */}
                                          {fieldLengthErrors.amendments && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {fieldLengthErrors.amendments}
                                             </Typography>
                                          )}
                                          {/* {touched.amendments && errors.amendments && (
                                             <Typography
                                                variant="caption"
                                                sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}
                                             >
                                                {errors.amendments}
                                             </Typography>
                                          )} */}
                                       </>)
                                    }
                                    }
                                 </Field>
                                 {/* <ErrorMessage
                                    name="amendments"
                                    component="div"
                                    style={{ color: 'red' }}
                                 /> */}
                              </Grid>
                           </Grid>
                        </Grid>

                        {/* Client Info - 4 columns */}
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={
                                 <span>
                                    Project Name <span style={{ color: '#d32f2f' }}>*</span>
                                 </span>
                              }
                              name="projectName"
                              value={values.projectName}
                              onChange={(e) => setFieldValue('projectName', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.projectName && errors.projectName)}
                              helperText={touched.projectName && errors.projectName}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Client Name</span>}
                              name="client_name"
                              value={values.client_name}
                              onChange={(e) => setFieldValue('client_name', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.client_name && errors.client_name)}
                              helperText={touched.client_name && errors.client_name}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Designation</span>}
                              name="designation"
                              value={values.designation}
                              onChange={(e) => setFieldValue('designation', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.designation && errors.designation)}
                              helperText={touched.designation && errors.designation}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Company Name</span>}
                              name="companyName"
                              value={values.companyName}
                              onChange={(e) => setFieldValue('companyName', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.companyName && errors.companyName)}
                              helperText={touched.companyName && errors.companyName}
                           />
                        </Grid>
                        <Grid item xs={12} md={3}>
                           <TextField
                              fullWidth
                              label={<span>Client Address</span>}
                              name="address"
                              value={values.address}
                              onChange={(e) => setFieldValue('address', e.target.value)}
                              onBlur={handleBlur}
                              error={Boolean(touched.address && errors.address)}
                              helperText={touched.address && errors.address}
                           />
                        </Grid>
                     </Grid>
                  </Box>
               </Grid>
            </Grid>
         </Box>
      </div>
   );
}

export default StepOneUpdate;